import {Controller} from "stimulus"

/**
 * Stimulus controller for Stripe Financial Connections integration.
 * Handles creating FC sessions and launching the Stripe-hosted modal
 * for bank account linking.
 */
export default class extends Controller {
  initialize() {
    this._stripeInstances = {}
  }

  getStripe(publishableKey, stripeConnectAccountId) {
    if (typeof Stripe === "undefined") {
      console.error("Stripe.js not loaded")
      return null
    }

    // When acting on behalf of a Connect account, Stripe.js must be
    // initialized with the stripeAccount option so that client_secrets
    // created under that account are recognized.
    var cacheKey = stripeConnectAccountId || "_platform"
    if (!this._stripeInstances) this._stripeInstances = {}

    if (!this._stripeInstances[cacheKey]) {
      var options = stripeConnectAccountId ? {stripeAccount: stripeConnectAccountId} : {}
      this._stripeInstances[cacheKey] = Stripe(publishableKey, options)
    }
    return this._stripeInstances[cacheKey]
  }

  async startConnection(event) {
    var button = event.currentTarget
    var publishableKey = button.dataset.financialConnectionsPublishableKey
    var createSessionUrl = button.dataset.financialConnectionsCreateSessionUrl
    var stripeCustomerAccountId = button.dataset.financialConnectionsStripeCustomerAccountId
    var stripeConnectAccountId = button.dataset.financialConnectionsStripeConnectAccountId

    button.disabled = true
    button.textContent = "Connecting..."

    try {
      var stripe = this.getStripe(publishableKey, stripeConnectAccountId)
      if (!stripe) {
        alert("Stripe.js failed to load. Please refresh and try again.")
        return
      }

      var csrfToken = document.querySelector("meta[name=csrf-token]").content

      var response = await fetch(createSessionUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken
        },
        credentials: "same-origin",
        body: JSON.stringify({
          stripe_customer_account_id: stripeCustomerAccountId
        })
      })

      var data = await response.json()

      if (data.error) {
        alert("Error: " + data.error)
        return
      }

      var result = await stripe.collectFinancialConnectionsAccounts({
        clientSecret: data.client_secret
      })

      if (result.error) {
        console.error("[FinancialConnections] Connection failed:", result.error.message)
        alert("Connection failed: " + result.error.message)
        return
      }

      // POST to import_accounts to persist the linked accounts
      var importUrl = createSessionUrl.replace(/\/?$/, "") + "/import_accounts"

      var importResponse = await fetch(importUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "Accept": "text/html"
        },
        credentials: "same-origin",
        body: JSON.stringify({
          session_id: data.session_id,
          stripe_customer_account_id: stripeCustomerAccountId
        }),
        redirect: "follow"
      })

      // The server redirects to linked_accounts index on success
      window.location.href = importResponse.url || importResponse.headers.get("Location") || window.location.href
    } catch (error) {
      console.error("[FinancialConnections] Error:", error.message)
      alert("An error occurred while connecting your bank account: " + error.message)
    } finally {
      button.disabled = false
      button.textContent = "Connect Bank via Stripe"
    }
  }

  async snooze(event) {
    var button = event.currentTarget
    var snoozeUrl = button.dataset.financialConnectionsSnoozeUrl
    var bannerId = button.dataset.financialConnectionsBannerId
    var csrfToken = document.querySelector("meta[name=csrf-token]").content

    try {
      await fetch(snoozeUrl, {
        method: "POST",
        headers: {"X-CSRF-Token": csrfToken},
        credentials: "same-origin"
      })
      var banner = document.getElementById(bannerId)
      if (banner) banner.remove()
    } catch (error) {
      console.error("Error snoozing:", error)
    }
  }

  async reauthorize(event) {
    var button = event.currentTarget
    var publishableKey = button.dataset.financialConnectionsPublishableKey
    var reauthorizeUrl = button.dataset.financialConnectionsReauthorizeUrl
    var importUrl = button.dataset.financialConnectionsImportUrl
    var stripeCustomerAccountId = button.dataset.financialConnectionsStripeCustomerAccountId
    var stripeConnectAccountId = button.dataset.financialConnectionsStripeConnectAccountId

    button.disabled = true
    var originalText = button.textContent
    button.textContent = "Reconnecting..."

    try {
      var stripe = this.getStripe(publishableKey, stripeConnectAccountId)
      if (!stripe) {
        alert("Stripe.js failed to load. Please refresh and try again.")
        return
      }

      var csrfToken = document.querySelector("meta[name=csrf-token]").content

      var response = await fetch(reauthorizeUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken
        },
        credentials: "same-origin"
      })

      var data = await response.json()

      if (data.error) {
        alert("Error: " + data.error)
        return
      }

      var result = await stripe.collectFinancialConnectionsAccounts({
        clientSecret: data.client_secret
      })

      if (result.error) {
        console.error("Reauthorization error:", result.error)
        alert("Reconnection failed: " + result.error.message)
        return
      }

      // POST to import_accounts to persist the reauthorized session data
      var importResponse = await fetch(importUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "Accept": "text/html"
        },
        credentials: "same-origin",
        body: JSON.stringify({
          session_id: data.session_id,
          stripe_customer_account_id: stripeCustomerAccountId
        }),
        redirect: "follow"
      })

      window.location.href = importResponse.url || importResponse.headers.get("Location") || window.location.href
    } catch (error) {
      console.error("Error reauthorizing:", error)
      alert("An error occurred while reconnecting your bank account.")
    } finally {
      button.disabled = false
      button.textContent = originalText
    }
  }
}
